import { Component, ViewEncapsulation, OnDestroy, HostBinding } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, Observable, of, map, switchMap } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { ProductTranslationService, PRODUCT_NAMESPACE_RTPC_FIELD } from '../../../services/product-translation.service';
import { ConfigurationService } from '../../../services/configuration.api.service';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { UserService } from '../../../../shared/services/user.service';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { BackButtonComponent } from '../../../../shared/components/back-button/back-button.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { ScrollerDirective } from '../../../../shared/directives/scroller.directive';

import { buildProductCardFields } from './utils/product-card-fields-builder';
import { FieldObjectCst } from './utils/field/field-object';
import { ProductCardContentComponent } from './product-card-content.component';

@Component({
	selector: 'copo-product-card',
	templateUrl: './product-card.component.html',
	styleUrl: './product-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		LoadingSpinnerComponent,
		BackButtonComponent,
		MatIconModule,
		MatButtonModule,
		TranslateModule,
		ProductCardContentComponent,
	],
})
export class ProductCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	isLoading = true;
	isError = false;
	isEmpty = false;

	userPartReference: string | undefined;
	subcategory?: string;
	form?: string;
	productFieldsObs?: Observable<FieldObjectCst>;
	productConfiguration!: Subscription;
	constructor(
		private readonly configurationService: ConfigurationService,
		private readonly productTranslationService: ProductTranslationService,
		private readonly userService: UserService,
		private readonly route: ActivatedRoute,
	) {
		const inquiryId = <string> this.route.snapshot.params['id'];
		this.loadConfiguration(inquiryId);
	}

	ngOnDestroy() {
		this.productConfiguration.unsubscribe();
	}

	translate(value: string) {
		return this.productTranslationService.translate(`${ PRODUCT_NAMESPACE_RTPC_FIELD }${ value }`);
	}

	private getBocsUserValue(userValue: string, userList: BocClient[]) {
		for (const list of userList) {
			if (list.businessId === userValue) {
				return `${ list.name } / ${ list.city }`;
			}
		}
		return '';
	}

	private loadConfiguration(inquiryId: string) {
		let soldTo: string;
		let shipTo: string;
		let endUser: string;
		this.productConfiguration = this.configurationService
			.getConfigurationById(inquiryId, 'INLINE')
			.pipe(
				switchMap(product => {
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
					if (product === null || product === undefined) {
						return of(null);
					}
					soldTo = Number(product.soldToParty).toString() || UNDERSCORE;
					shipTo = Number(product.shipToParty).toString() || UNDERSCORE;
					endUser = Number(product.endUser).toString() || UNDERSCORE;
					const businessIds = [soldTo, shipTo, endUser];
					return this.userService.getBocs(
						['businessId', 'name', 'city', 'address', 'country', 'postalCode'],
						businessIds.map(x => `businessId:${ x }`).join(' OR ')
					).pipe(
						map(v => ({ product, bocs: v.results }))
					);
				})
			)
			.subscribe({
				next: result => {
					if (result === null) {
						this.isEmpty = true;
						this.isLoading = false;
						return;
					}
					const { product, bocs } = result;
					this.userPartReference = product.usePartReference;
					this.subcategory = product.subcategory || UNDERSCORE;
					this.form = product.form || UNDERSCORE;
					this.isLoading = false;
					this.productFieldsObs = of(buildProductCardFields(
						product,
						this.getBocsUserValue(soldTo, bocs),
						this.getBocsUserValue(shipTo, bocs),
						this.getBocsUserValue(endUser, bocs)
					));
				},
				error: (err: unknown) => {
					this.isError = true;
					this.isLoading = false;
					console.error('unable to load configurationa and field names', err);
				}
			});
	}
}
